<template>
  <el-dialog
    :model-value="modelValue"
    title="安全验证（自动）"
    width="420px"
    :align-center="true"
    :close-on-click-modal="false"
    @update:model-value="onVisible"
  >
    <div class="wv-body">
      <p class="wv-hint">
        页面已在服务端用微信环境打开。<strong>请在下方图像里拖动滑块</strong>完成验证——
        像在手机上一样按住滑块拖到底即可，无需手机扫码。
      </p>

      <div
        v-if="state === 'loading'"
        class="wv-status"
        v-loading="true"
        element-loading-text="正在打开验证页…"
      />

      <div v-show="state === 'ready' || state === 'success'" class="wv-stage">
        <img
          ref="frameEl"
          class="wv-frame"
          :src="frameSrc"
          draggable="false"
          @pointerdown="onPointer('down', $event)"
          @pointermove="onPointer('move', $event)"
          @pointerup="onPointer('up', $event)"
          @pointercancel="onPointer('up', $event)"
        />
      </div>

      <div v-if="state === 'success'" class="wv-ok">✅ 验证已完成，正在继续登录…</div>
      <div v-if="state === 'failed' || state === 'expired'" class="wv-fail">
        {{ errorText || "自动验证不可用" }}<br />
        <span class="wv-fallback">已切回手动扫码，请用微信扫码完成。</span>
      </div>
    </div>

    <template #footer>
      <el-button @click="useManual">改用手动扫码</el-button>
      <el-button type="primary" :disabled="state !== 'success'" @click="onVisible(false)">完成</el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { ref, watch, onBeforeUnmount, nextTick } from "vue";
import { ElMessage } from "element-plus";
import { startWebVerify, stopWebVerify, getWebVerifyFrame, postWebVerifyPointer } from "../api/http";

const props = defineProps<{ modelValue: boolean; url: string }>();
const emit = defineEmits<{
  (e: "update:modelValue", v: boolean): void;
  (e: "completed"): void;   // verification done -> parent re-polls login
  (e: "fallback"): void;    // auto path unavailable -> parent shows manual QR
}>();

type State = "idle" | "loading" | "ready" | "success" | "failed" | "expired";
const state = ref<State>("idle");
const frameSrc = ref("");
const errorText = ref("");
const frameEl = ref<HTMLImageElement | null>(null);

let sessionId = "";
let pollTimer: number | null = null;
let seq = 0;
let dragging = false;
let lastMoveSent = 0;
let succeeded = false;

function reset() {
  if (pollTimer !== null) { clearTimeout(pollTimer); pollTimer = null; }
  if (sessionId) { void stopWebVerify(sessionId).catch(() => {}); sessionId = ""; }
  state.value = "idle";
  frameSrc.value = "";
  errorText.value = "";
  dragging = false;
  seq = 0;
}

async function begin() {
  state.value = "loading";
  errorText.value = "";
  try {
    const res = await startWebVerify(props.url);
    if (!res.successful || !res.result?.sessionId) {
      // No `url` field / expired ticket / decrypt failed -> fall back to manual.
      fallbackTo(res.message || "无法自动打开验证页");
      return;
    }
    sessionId = res.result.sessionId;
    poll();
  } catch (e) {
    fallbackTo((e as Error)?.message || "自动验证启动失败");
  }
}

async function poll() {
  if (!sessionId) return;
  try {
    const res = await getWebVerifyFrame(sessionId, seq);
    if (res.successful && res.result) {
      const f = res.result;
      if (f.frame) { frameSrc.value = "data:image/jpeg;base64," + f.frame; seq = f.seq; }
      if (f.state === "ready") { if (state.value === "loading") state.value = "ready"; }
      else if (f.state === "success") { state.value = "success"; onSuccess(); return; }
      else if (f.state === "failed" || f.state === "expired") { fallbackTo(f.message || "验证会话失败"); return; }
    } else if (!res.successful) {
      fallbackTo(res.message || "验证会话已结束");
      return;
    }
  } catch {
    /* transient — keep polling */
  }
  // ~14 fps while the operator watches/drags.
  pollTimer = window.setTimeout(poll, 70);
}

function onSuccess() {
  if (succeeded) return;
  succeeded = true;
  if (pollTimer !== null) { clearTimeout(pollTimer); pollTimer = null; }
  ElMessage({ message: "验证已完成", type: "success" });
  setTimeout(() => { emit("completed"); onVisible(false); }, 1200);
}

function fallbackTo(reason: string) {
  errorText.value = reason;
  state.value = "failed";
  emit("fallback");
}

function onPointer(kind: "down" | "move" | "up", ev: PointerEvent) {
  if (state.value !== "ready") return;
  const el = frameEl.value;
  if (!el) return;
  const rect = el.getBoundingClientRect();
  const x = Math.max(0, Math.min(1, (ev.clientX - rect.left) / rect.width));
  const y = Math.max(0, Math.min(1, (ev.clientY - rect.top) / rect.height));
  if (kind === "down") { dragging = true; try { el.setPointerCapture(ev.pointerId); } catch { /* ignore */ } }
  else if (kind === "move") { if (!dragging) return; const now = Date.now(); if (now - lastMoveSent < 20) return; lastMoveSent = now; }
  else { if (!dragging) return; dragging = false; }
  void postWebVerifyPointer(sessionId, kind, x, y).catch(() => {});
  ev.preventDefault();
}

function useManual() {
  emit("fallback");
  onVisible(false);
}

function onVisible(v: boolean) {
  emit("update:modelValue", v);
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) { succeeded = false; nextTick(begin); }
    else reset();
  },
);

onBeforeUnmount(reset);
</script>

<style scoped>
.wv-body { text-align: center; }
.wv-hint { font-size: 13px; color: #555; margin: 0 0 12px; line-height: 1.5; }
.wv-status { min-height: 200px; }
.wv-stage { display: flex; justify-content: center; }
.wv-frame {
  width: 320px;
  max-width: 100%;
  border: 1px solid #eee;
  border-radius: 6px;
  touch-action: none;
  user-select: none;
  cursor: pointer;
}
.wv-ok { margin-top: 12px; color: #16a34a; font-weight: 600; }
.wv-fail { margin-top: 12px; color: #dc2626; font-size: 13px; line-height: 1.6; }
.wv-fallback { color: #888; }
</style>
